import React, { useState, useEffect, useMemo } from "react";
import PhotoReleaseQuestion, { usePhotoReleaseQuestion } from "../waivers/PhotoReleaseQuestion";
import { Input } from "../ui";
import { formatPartialNational } from "../../utils/phoneFormat";
import PhoneInput from "../ui/PhoneInput";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import PublicPaymentStep from "../common/PublicPaymentStep";
import {
  getPublicEventById,
  registerGuestForEvent,
  batchRegisterGuestForEvent,
  PublicEvent,
  GuestEventRegistrationData,
  BatchGuestEventRegistrationData,
} from "../../api/publicEventsAPI";
import {
  usePublicMemberLookup,
  isAthleteUuid,
} from "../../hooks/usePublicMemberLookup";
import {
  Calendar,
  MapPin,
  Users,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  ArrowLeft,
  Clock,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import SEOHead from "../seo/SEOHead";
import EventSchema from "../seo/EventSchema";
import BreadcrumbSchema from "../seo/BreadcrumbSchema";
import { postEmbedSignal } from "../../utils/embedSignals";
import { getPublicRegistrationOptions, addonsTotal, RegistrationOptions } from "../../api/eventAddonsAPI";
import EventExtrasPicker, { extrasProblem, usesExtras } from "./EventExtrasPicker";
import { safeSessionGetItem } from "@mediatechgroup/app-version";

interface Participant {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  athlete_id?: string;
}

// Form component (no Stripe context — payment lives in
// <PublicPaymentStep> which mounts its own <Elements>).
const RegistrationForm: React.FC<{
  event: PublicEvent;
  clubid: string;
  embed?: boolean;
}> = ({ event, clubid, embed }) => {

  // Detect embed dark theme from URL params or sessionStorage
  const isEmbedDark = useMemo(() => {
    if (!embed) return false;
    const params = new URLSearchParams(window.location.search);
    const theme = params.get("theme");
    if (theme) return theme === "dark";
    try {
      const stored = safeSessionGetItem("embedStyleOverrides");
      if (stored) return JSON.parse(stored).theme === "dark";
    } catch { /* ignore */ }
    return false;
  }, [embed]);

  const [step, setStep] = useState<"form" | "payment" | "success">("form");
  const [submitting, setSubmitting] = useState(false);
  // The club's photo / media question (optional; never blocks registering).
  const photo = usePhotoReleaseQuestion("public");
  const [registrationResult, setRegistrationResult] = useState<any>(null);
  // True when we landed on the success step via a Stripe redirect return
  // (3DS / SCA) rather than the inline flow — the in-memory participant
  // state is gone, so the success screen shows a generic confirmation.
  const [completedViaRedirect, setCompletedViaRedirect] = useState(false);

  // Contact info (shared)
  const [contactInfo, setContactInfo] = useState({
    contact_email: "",
    contact_phone: "",
    contact_name: "",
  });

  // Multi-participant state
  const [participants, setParticipants] = useState<Participant[]>([
    { first_name: "", last_name: "", date_of_birth: "", gender: "" },
  ]);

  // Notes (no emergency/medical for events)
  const [notes, setNotes] = useState("");

  const [selectedPricing, setSelectedPricing] = useState(
    event.pricing_options?.[0] || null
  );

  // Time slot and add-ons (PM 4e4384e4). Loaded for every event; most offer
  // neither and the picker stays hidden. A failed load is shown, never read
  // as "nothing to choose": the server would refuse a required choice.
  const [extrasOptions, setExtrasOptions] = useState<RegistrationOptions | null>(null);
  const [extrasLoadError, setExtrasLoadError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState("");
  const [addonIds, setAddonIds] = useState<string[]>([]);
  const hasExtras = usesExtras(extrasOptions);

  useEffect(() => {
    let cancelled = false;
    getPublicRegistrationOptions(clubid, event.id)
      .then((opts) => {
        if (!cancelled) setExtrasOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setExtrasLoadError("We could not load this event's times and options. Please refresh the page.");
      });
    return () => {
      cancelled = true;
    };
  }, [clubid, event.id]);

  // Member/customer identity. The lookup is a two-step dance: /lookup-member
  // returns only requires_password=true, then the visitor enters their password
  // and /verify-member releases PII. That password gate is a SECURITY boundary,
  // and it lives in one shared hook rather than a copy per public page — see
  // hooks/usePublicMemberLookup.
  const {
    emailLookupLoading,
    memberFound,
    accountType,
    membershipId,
    memberToken,
    memberAthletes,
    selectedAthleteIds,
    passwordPromptOpen,
    verifyPassword,
    verifyError,
    verifyLoading,
    setVerifyPassword,
    setSelectedAthleteIds,
    resetMemberState,
    handleEmailBlur,
    handleVerifyPassword,
    handleSkipPasswordPrompt,
    toggleAthleteSelection,
  } = usePublicMemberLookup({
    clubid: clubid || "",
    resource: "events",
    setContactInfo,
    setParticipants,
  });

  // Handle the return from a Stripe redirect (3DS / SCA). confirmPayment
  // navigates to return_url (this page) with `payment_intent`,
  // `payment_intent_client_secret`, and `redirect_status` appended.
  // Event fulfillment is webhook-driven (no inline confirm endpoint), so
  // here we just surface the confirmation and let the webhook reconcile.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentIntentId = params.get("payment_intent");
    const redirectStatus = params.get("redirect_status");
    if (!paymentIntentId || !redirectStatus) return;

    const cleanUrl = window.location.pathname + (window.location.hash || "");
    window.history.replaceState({}, "", cleanUrl);

    if (redirectStatus === "succeeded" || redirectStatus === "processing") {
      setCompletedViaRedirect(true);
      setStep("success");
    } else {
      toast.error("Payment was not completed. Please try registering again.");
    }
  }, [clubid, event.id]);

  const handleContactChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setContactInfo((prev) => ({ ...prev, [name]: value }));
    if (name === "contact_email") {
      resetMemberState();
    }
  };


  const addManualParticipant = () => {
    if (hasExtras) {
      toast.error("Please register one participant at a time for this event.");
      return;
    }
    if (participants.length >= 10) {
      toast.error("Maximum 10 participants allowed");
      return;
    }
    setParticipants((prev) => [
      ...prev,
      { first_name: "", last_name: "", date_of_birth: "", gender: "" },
    ]);
  };

  const updateParticipant = (index: number, field: keyof Participant, value: string) => {
    setParticipants((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeParticipant = (index: number) => {
    const p = participants[index];
    if (p.athlete_id) {
      setSelectedAthleteIds((prev) => {
        const next = new Set(prev);
        next.delete(p.athlete_id!);
        return next;
      });
    }
    setParticipants((prev) => prev.filter((_, i) => i !== index));
  };

  // Build effective participant list from athletes + manual entries
  const effectiveParticipants = participants;

  const getTotalAmount = (): number => {
    const unitPrice = selectedPricing?.amount || 0;
    const extras = extrasOptions ? addonsTotal(extrasOptions.addons, addonIds) : 0;
    return Math.round((unitPrice * Math.max(effectiveParticipants.length, 1) + extras) * 100) / 100;
  };

  const validateForm = () => {
    if (!contactInfo.contact_email) {
      toast.error("Contact email is required");
      return false;
    }

    // Email matched an existing member/customer but the visitor
    // hasn't verified yet — block so they can authenticate (see
    // PublicCampPage for the why).
    if (passwordPromptOpen && !memberFound) {
      toast.error("This email already has an account. Enter your password above to continue, or use a different email.");
      return false;
    }

    if (effectiveParticipants.length === 0) {
      toast.error("At least one participant is required");
      return false;
    }

    if (hasExtras && effectiveParticipants.length > 1) {
      toast.error("Please register one participant at a time for this event.");
      return false;
    }
    const extrasIssue = extrasProblem(extrasOptions, sessionId, addonIds);
    if (extrasIssue) {
      toast.error(extrasIssue);
      return false;
    }

    for (let i = 0; i < effectiveParticipants.length; i++) {
      const p = effectiveParticipants[i];
      const label = p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : `Participant ${i + 1}`;
      if (!p.first_name || !p.last_name) {
        toast.error(`${label}: first and last name are required`);
        return false;
      }
      if (!p.date_of_birth) {
        toast.error(`${label}: date of birth is required`);
        return false;
      }
      if (!p.gender) {
        toast.error(`${label}: gender is required`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const N = effectiveParticipants.length;

      if (N === 1) {
        // Single participant -> use existing endpoint
        const p = effectiveParticipants[0];
        const registrationData: GuestEventRegistrationData = {
          contact_email: contactInfo.contact_email,
          contact_phone: contactInfo.contact_phone,
          contact_name: contactInfo.contact_name || `${p.first_name} ${p.last_name}`,
          first_name: p.first_name,
          last_name: p.last_name,
          date_of_birth: p.date_of_birth,
          gender: p.gender,
          pricing_option: selectedPricing || undefined,
          notes: notes || undefined,
          membership_id: membershipId || undefined,
          member_token: memberToken || undefined,
          // By id only: the server prices them (services/eventAddons).
          ...(sessionId ? { session_id: sessionId } : {}),
          ...(addonIds.length > 0 ? { addons: addonIds } : {}),
        };

        const response = await registerGuestForEvent(clubid, event.id, { ...registrationData, ...photo.payload });

        if (response?.success) {
          setRegistrationResult(response);
          if (response.clientSecret) {
            setStep("payment");
          } else {
            setStep("success");
            toast.success(response.message || "Successfully registered!");
          }
        } else {
          toast.error(response?.message || "Registration failed");
        }
      } else {
        // Multi-participant -> use batch endpoint
        const batchData: BatchGuestEventRegistrationData = {
          contact_email: contactInfo.contact_email,
          contact_phone: contactInfo.contact_phone,
          contact_name: contactInfo.contact_name || `${effectiveParticipants[0].first_name} ${effectiveParticipants[0].last_name}`,
          participants: effectiveParticipants.map((p) => ({
            first_name: p.first_name,
            last_name: p.last_name,
            date_of_birth: p.date_of_birth,
            gender: p.gender,
            athlete_id: p.athlete_id,
          })),
          pricing_option: selectedPricing || undefined,
          notes: notes || undefined,
          membership_id: membershipId || undefined,
          member_token: memberToken || undefined,
        };

        const response = await batchRegisterGuestForEvent(clubid, event.id, { ...batchData, ...photo.payload });

        if (response?.success) {
          setRegistrationResult(response);
          if (response.clientSecret) {
            setStep("payment");
          } else {
            setStep("success");
            toast.success(response.message || "Successfully registered!");
          }
        } else {
          toast.error(response?.message || "Registration failed");
        }
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || "Registration failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Payment is handled by <PublicPaymentStep> below.

  if (step === "success") {
    if (embed) postEmbedSignal("clubcentral:complete", { widget: "event" });
    const names = effectiveParticipants.map((p) => `${p.first_name} ${p.last_name}`).join(", ");
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Registration Complete!</h3>
        {completedViaRedirect ? (
          <p className="text-gray-600 mb-2">
            Your payment was received and your registration for {event.name} is confirmed.
          </p>
        ) : (
          <>
            <p className="text-gray-600 mb-2">
              {effectiveParticipants.length === 1
                ? `${names} has been registered for ${event.name}.`
                : `${effectiveParticipants.length} participants have been registered for ${event.name}.`}
            </p>
            {effectiveParticipants.length > 1 && (
              <p className="text-gray-500 text-sm mb-2">{names}</p>
            )}
          </>
        )}
        <p className="text-gray-500 text-sm mb-6">
          {completedViaRedirect
            ? "A confirmation email is on its way."
            : `A confirmation email will be sent to ${contactInfo.contact_email}.`}
        </p>
        <Link
          to={embed ? `/${clubid}/embed/events` : `/${clubid}/events`}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Link>
      </div>
    );
  }

  if (step === "payment" && registrationResult?.clientSecret) {
    const totalAmt = getTotalAmount();
    const p0 = effectiveParticipants[0];
    return (
      <PublicPaymentStep
        clientSecret={registrationResult.clientSecret}
        totalAmt={totalAmt}
        participantCount={effectiveParticipants.length}
        unitPrice={selectedPricing?.amount}
        billingName={
          contactInfo.contact_name || `${p0?.first_name || ""} ${p0?.last_name || ""}`.trim() || undefined
        }
        billingEmail={contactInfo.contact_email}
        isEmbedDark={isEmbedDark}
        // Events don't have a frontend confirm-payment endpoint —
        // the Stripe webhook reconciles. Skip onPaymentSucceeded.
        onComplete={() => setStep("success")}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Contact Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label
              htmlFor="event-contact-email"
              className={`block text-sm font-medium mb-1 ${isEmbedDark ? "text-gray-300" : "text-gray-700"}`}
            >
              Email *
            </label>
            <div className="relative">
              <input
                id="event-contact-email"
                type="email"
                name="contact_email"
                value={contactInfo.contact_email}
                onChange={handleContactChange}
                onBlur={() => handleEmailBlur(contactInfo.contact_email)}
                className={`w-full border rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500 ${
                  isEmbedDark
                    ? "border-gray-600 bg-gray-900 text-gray-100"
                    : "border-gray-300"
                }`}
                required
                placeholder="Enter your email to check for existing membership"
              />
              {emailLookupLoading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-blue-500" />
              )}
            </div>
            {passwordPromptOpen && !memberFound && (
              <div className={`mt-2 p-3 rounded-md border ${isEmbedDark ? "border-blue-700 bg-blue-950/20" : "border-blue-200 bg-blue-50"}`}>
                <p className={`text-sm mb-2 ${isEmbedDark ? "text-gray-100" : "text-gray-800"}`}>
                  We found an existing account for this email. Enter its password to register with that account (use Forgot password on the sign-in page if you need a new one), or use a different email address to register as a new visitor.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="password"
                    aria-label="Password"
                    value={verifyPassword}
                    onChange={(e) => setVerifyPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleVerifyPassword(contactInfo.contact_email);
                      }
                    }}
                    placeholder="Password"
                    autoComplete="current-password"
                    className={`flex-1 border rounded-md px-3 py-2 text-sm ${
                      isEmbedDark
                        ? "border-gray-600 bg-gray-900 text-gray-100"
                        : "border-gray-300"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => handleVerifyPassword(contactInfo.contact_email)}
                    disabled={verifyLoading || !verifyPassword}
                    className="px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {verifyLoading ? "Verifying…" : "Verify"}
                  </button>
                  <button
                    type="button"
                    onClick={handleSkipPasswordPrompt}
                    className={`px-3 py-2 rounded-md text-sm ${
                      isEmbedDark
                        ? "text-gray-300 hover:bg-gray-800"
                        : "text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Skip
                  </button>
                </div>
                {verifyError && (
                  <p className="mt-2 text-xs text-red-600">{verifyError}</p>
                )}
              </div>
            )}
          </div>
          <div>
            <Input
              label="Your Name"
              type="text"
              name="contact_name"
              value={contactInfo.contact_name}
              onChange={handleContactChange}
              className="border-gray-300"
              placeholder="If different from participant"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <PhoneInput
              name="contact_phone"
              value={contactInfo.contact_phone}
              onChange={(d) =>
                handleContactChange({
                  target: { name: "contact_phone", value: formatPartialNational(d) },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Member/Customer Found Banner */}
      {memberFound && (
        <div className={`rounded-lg border p-4 flex items-start gap-3 ${
          isEmbedDark
            ? "bg-green-900/30 border-green-700"
            : "bg-green-50 border-green-200"
        }`}>
          <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isEmbedDark ? "text-green-400" : "text-green-600"}`} />
          <div>
            <p className={`font-medium ${isEmbedDark ? "text-green-300" : "text-green-800"}`}>
              Welcome back{contactInfo.contact_name ? `, ${contactInfo.contact_name.split(" ")[0]}` : ""}!
            </p>
            <p className={`text-sm ${isEmbedDark ? "text-green-400" : "text-green-600"}`}>
              {accountType === "customer"
                ? memberAthletes.length > 0
                  ? "We found your account. Select from your previous participants or register someone new."
                  : "We found your account."
                : "We found your membership. Select participants below."}
            </p>
          </div>
        </div>
      )}

      {/* Athlete Selector - Checkboxes for multi-select */}
      {memberFound && memberAthletes.length > 0 && (
        <div className={`rounded-lg border p-6 ${
          isEmbedDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${isEmbedDark ? "text-gray-100" : "text-gray-900"}`}>
            Select Participants
          </h3>
          <div className="space-y-2">
            {memberAthletes.map((athlete) => {
              const isSelected = selectedAthleteIds.has(athlete.id);

              return (
                <label
                  key={athlete.id}
                  className={`flex items-center p-3 border rounded-lg transition-colors cursor-pointer ${
                    isSelected
                      ? isEmbedDark
                        ? "border-blue-500 bg-blue-900/30"
                        : "border-blue-500 bg-blue-50"
                      : isEmbedDark
                        ? "border-gray-600 hover:border-gray-500"
                        : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleAthleteSelection(athlete)}
                    className="mr-3 w-4 h-4 rounded"
                  />
                  <span className={`font-medium flex-1 ${isEmbedDark ? "text-gray-100" : "text-gray-900"}`}>
                    {athlete.first_name} {athlete.last_name}
                    {athlete.date_of_birth && (
                      <span className={`text-sm font-normal ml-2 ${isEmbedDark ? "text-gray-400" : "text-gray-500"}`}>
                        (DOB: {athlete.date_of_birth})
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <CheckCircle2 className="w-5 h-5 text-blue-500 ml-2 flex-shrink-0" />
                  )}
                </label>
              );
            })}
          </div>
          <button
            type="button"
            onClick={addManualParticipant}
            className={`mt-3 w-full p-2.5 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
              isEmbedDark
                ? "border-gray-600 text-gray-400 hover:border-gray-500"
                : "border-gray-300 text-gray-500 hover:border-gray-400"
            }`}
          >
            <Plus className="w-4 h-4" />
            Register someone new
          </button>
        </div>
      )}

      {/* Participant Information - Cards for each participant */}
      {(!memberFound || memberAthletes.length === 0) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {participants.length > 1 ? "Participants" : "Participant Information"}
          </h3>

          <div className="space-y-4">
            {participants.map((p, index) => (
              <div key={index} className={`${participants.length > 1 ? "p-4 bg-gray-50 rounded-lg border border-gray-100" : ""}`}>
                {participants.length > 1 && (
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm font-medium text-gray-700">
                      {p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : `Participant ${index + 1}`}
                    </span>
                    {index > 0 && (
                      <button aria-label={`Remove participant ${index + 1}`}
                        type="button"
                        onClick={() => removeParticipant(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      label="First Name *"
                      type="text"
                      value={p.first_name}
                      onChange={(e) => updateParticipant(index, "first_name", e.target.value)}
                      className="border-gray-300"
                      required={index === 0}
                    />
                  </div>
                  <div>
                    <Input
                      label="Last Name *"
                      type="text"
                      value={p.last_name}
                      onChange={(e) => updateParticipant(index, "last_name", e.target.value)}
                      className="border-gray-300"
                      required={index === 0}
                    />
                  </div>
                  <div>
                    <Input
                      label="Date of Birth *"
                      type="date" autoComplete="off"
                      value={p.date_of_birth}
                      onChange={(e) => updateParticipant(index, "date_of_birth", e.target.value)}
                      className="border-gray-300"
                      required={index === 0}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender *
                    </label>
                    <select
                      value={p.gender}
                      onChange={(e) => updateParticipant(index, "gender", e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      required={index === 0}
                    >
                      <option value="">Select...</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      {event.allow_other_gender && (
                        <>
                          <option value="other">Other</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!hasExtras && (
          <button
            type="button"
            onClick={addManualParticipant}
            className="mt-4 w-full p-2.5 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 flex items-center justify-center gap-2 text-sm font-medium hover:border-gray-400 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Another Participant
          </button>
          )}
        </div>
      )}

      {/* Manual participant cards when member found. Same dual-filter as PublicCampPage:
          exclude rows by athlete_id (UUID case) AND by synthetic key (no-UUID case). */}
      {(() => {
        const syntheticSelectedKeys = new Set(
          memberAthletes
            .filter((a) => selectedAthleteIds.has(a.id) && !isAthleteUuid(a.id))
            .map((a) => `${a.first_name}-${a.last_name}-${a.date_of_birth}`)
        );
        const renderable = participants.filter(
          (p) => !p.athlete_id && !syntheticSelectedKeys.has(`${p.first_name}-${p.last_name}-${p.date_of_birth}`)
        );
        if (!memberFound || renderable.length === 0) return null;
        return (
          <div className={`rounded-lg border p-6 ${isEmbedDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isEmbedDark ? "text-gray-100" : "text-gray-900"}`}>
              Additional Participants
            </h3>
            <div className="space-y-4">
              {participants.map((p, index) => {
                if (p.athlete_id) return null;
                if (syntheticSelectedKeys.has(`${p.first_name}-${p.last_name}-${p.date_of_birth}`)) return null;
                return (
                <div key={`manual-${index}`} className={`p-4 rounded-lg border ${isEmbedDark ? "border-gray-600 bg-gray-700" : "border-gray-100 bg-gray-50"}`}>
                  <div className="flex justify-between items-center mb-3">
                    <span className={`text-sm font-medium ${isEmbedDark ? "text-gray-200" : "text-gray-700"}`}>
                      {p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : "New Participant"}
                    </span>
                    <button aria-label={`Remove participant ${index + 1}`}
                      type="button"
                      onClick={() => removeParticipant(index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      aria-label={`Participant ${index + 1} first name`}
                      placeholder="First Name *"
                      value={p.first_name}
                      onChange={(e) => updateParticipant(index, "first_name", e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isEmbedDark ? "border-gray-600 bg-gray-800 text-gray-100" : "border-gray-300"
                      }`}
                    />
                    <input
                      type="text"
                      aria-label={`Participant ${index + 1} last name`}
                      placeholder="Last Name *"
                      value={p.last_name}
                      onChange={(e) => updateParticipant(index, "last_name", e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isEmbedDark ? "border-gray-600 bg-gray-800 text-gray-100" : "border-gray-300"
                      }`}
                    />
                    <input
                      type="date" autoComplete="off"
                      aria-label={`Participant ${index + 1} date of birth`}
                      value={p.date_of_birth}
                      onChange={(e) => updateParticipant(index, "date_of_birth", e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isEmbedDark ? "border-gray-600 bg-gray-800 text-gray-100" : "border-gray-300"
                      }`}
                    />
                    <select
                      value={p.gender}
                      onChange={(e) => updateParticipant(index, "gender", e.target.value)}
                      className={`w-full border rounded-md px-3 py-2 text-sm ${
                        isEmbedDark ? "border-gray-600 bg-gray-800 text-gray-100" : "border-gray-300"
                      }`}
                    >
                      <option value="">Gender *</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      {event.allow_other_gender && (
                        <>
                          <option value="other">Other</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        );
      })()}

      {/* Time slot and add-ons (PM 4e4384e4) */}
      {extrasLoadError && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">
          {extrasLoadError}
        </div>
      )}
      {extrasOptions && hasExtras && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <EventExtrasPicker
            options={extrasOptions}
            sessionId={sessionId}
            onSessionChange={setSessionId}
            addonIds={addonIds}
            onAddonsChange={setAddonIds}
          />
          {getTotalAmount() > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
              <span className="text-gray-600">Total</span>
              <span className="text-lg font-bold text-gray-900">${getTotalAmount().toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {/* Pricing Option */}
      {event.pricing_options && event.pricing_options.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Registration Option</h3>
          <div className="space-y-3">
            {event.pricing_options.map((option, index) => (
              <label
                key={index}
                className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedPricing?.label === option.label
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="pricing"
                    checked={selectedPricing?.label === option.label}
                    onChange={() => setSelectedPricing(option)}
                    className="mr-3"
                  />
                  <span className="text-gray-900 font-medium">{option.label}</span>
                </div>
                <span className="text-gray-700 font-semibold">
                  {option.amount > 0 ? `$${option.amount.toFixed(2)}` : "Free"}
                </span>
              </label>
            ))}
          </div>

          {/* Pricing summary for multiple participants */}
          {effectiveParticipants.length > 1 && selectedPricing && selectedPricing.amount > 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  {effectiveParticipants.length} participants x ${selectedPricing.amount.toFixed(2)}
                </span>
                <span className="text-lg font-bold text-gray-900">
                  ${getTotalAmount().toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Additional Notes */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h3>
        <textarea
          aria-label="Additional notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          placeholder="Any additional information or special requests"
        />
      </div>

      <PhotoReleaseQuestion photo={photo} />

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] rounded-lg font-medium disabled:opacity-50 transition-colors text-lg"
      >
        {submitting
          ? "Registering..."
          : getTotalAmount() > 0
            ? `Register & Pay $${getTotalAmount().toFixed(2)}`
            : "Register"}
      </button>
    </form>
  );
};

// Main page component
interface PublicEventPageProps {
  embed?: boolean;
}

const PublicEventPage: React.FC<PublicEventPageProps> = ({ embed }) => {
  const { clubid, eventId } = useParams<{ clubid: string; eventId: string }>();
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (clubid && eventId) {
      fetchEvent();
    }
  }, [clubid, eventId]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPublicEventById(clubid!, eventId!);
      if (response?.success) {
        setEvent(response.data);
      } else {
        setError("Event not found");
      }
    } catch {
      setError("Failed to load event. This event may not exist or is no longer available.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const [year, month, day] = dateString.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const isFull = event
    ? event.max_registrations
      ? event.current_registrations >= event.max_registrations && !event.waitlist_enabled
      : false
    : false;

  const firstDate = event?.dates?.[0];

  if (loading) {
    return (
      <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
        {!embed && <Navbar />}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 dark:border-gray-500"></div>
        </div>
        {!embed && <Footer />}
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
        {!embed && <Navbar />}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-500 mb-2">Event Not Found</h2>
            <p className="text-gray-400 mb-4">{error}</p>
            <Link
              to={embed ? `/${clubid}/embed/events` : `/${clubid}/events`}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Browse all events
            </Link>
          </div>
        </div>
        {!embed && <Footer />}
      </div>
    );
  }

  return (
    <div className={`flex flex-col min-h-screen ${embed ? "" : "bg-gray-50"}`}>
      {!embed && (
        <>
          <SEOHead
            title={event.name}
            description={event.description?.substring(0, 160)}
            ogType="event"
          />
          <BreadcrumbSchema
            items={[
              { name: "Events", path: "/events" },
              { name: event.name, path: `/events/${eventId}` },
            ]}
          />
          <EventSchema
            name={event.name}
            description={event.description}
            startDate={firstDate?.date}
            endDate={firstDate?.date}
            location={firstDate?.location}
            organizerName={undefined}
          />
          <Navbar />
        </>
      )}
      <div className="flex-1 max-w-6xl mx-auto px-4 py-8 w-full">
        {/* Back link */}
        <Link
          to={embed ? `/${clubid}/embed/events` : `/${clubid}/events`}
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to events
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Event Details - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {event.cover_image_url && (
              <img
                src={event.cover_image_url}
                alt={event.name}
                className="w-full h-64 object-cover rounded-lg"
              />
            )}

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{event.name}</h1>

              <div className="flex flex-wrap gap-2 mb-4">
                {event.category && (
                  <span
                    className="px-2 py-1 text-xs font-medium rounded-full"
                    style={{
                      backgroundColor: `${event.category.color}20`,
                      color: event.category.color,
                    }}
                  >
                    {event.category.name}
                  </span>
                )}
                {event.members_only && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                    Members Only
                  </span>
                )}
              </div>

              {event.description && (
                <p className="text-gray-600 whitespace-pre-wrap">{event.description}</p>
              )}
            </div>

            {/* Dates */}
            {event.dates && event.dates.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-blue-500" />
                  Schedule
                </h2>
                <div className="space-y-3">
                  {event.dates.map((dateItem, index) => (
                    <div
                      key={index}
                      className="flex items-start p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatDate(dateItem.date)}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          {dateItem.start_time && (
                            <span className="flex items-center">
                              <Clock className="w-3.5 h-3.5 mr-1" />
                              {formatTime(dateItem.start_time)}
                              {dateItem.end_time && ` - ${formatTime(dateItem.end_time)}`}
                            </span>
                          )}
                          {dateItem.location && (
                            <span className="flex items-center">
                              <MapPin className="w-3.5 h-3.5 mr-1" />
                              {dateItem.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Registration Sidebar - Right Column */}
          <div className="lg:col-span-1">
            {/* Quick Info Card */}
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
              <div className="space-y-3 text-sm">
                {event.dates && event.dates.length > 0 && (
                  <div className="flex items-start">
                    <Calendar className="w-4 h-4 mr-2 mt-0.5 text-gray-400" />
                    <div>
                      {event.dates.length === 1 ? (
                        <span>{formatDate(event.dates[0].date)}</span>
                      ) : (
                        <span>
                          {formatDate(event.dates[0].date)} - {formatDate(event.dates[event.dates.length - 1].date)}
                          <span className="text-gray-400 ml-1">({event.dates.length} dates)</span>
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {event.pricing_options && event.pricing_options.length > 0 && (
                  <div className="flex items-start">
                    <DollarSign className="w-4 h-4 mr-2 mt-0.5 text-gray-400" />
                    <div>
                      {event.pricing_options.map((p, i) => (
                        <div key={i}>
                          <span className="font-medium">{p.label}:</span>{" "}
                          {p.amount > 0 ? `$${p.amount.toFixed(2)}` : "Free"}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {event.max_registrations && (
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-2 text-gray-400" />
                    <span>
                      {event.current_registrations} / {event.max_registrations} registered
                    </span>
                  </div>
                )}

                {event.waitlist_enabled && event.max_registrations && event.current_registrations >= event.max_registrations && (
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2 text-amber-500" />
                    <span className="text-amber-600 font-medium">Waitlist available</span>
                  </div>
                )}
              </div>
            </div>

            {/* Registration blocked messages */}
            {isFull && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 font-medium">This event is full.</p>
              </div>
            )}

            {/* Registration Form or Members-Only CTA */}
            {!isFull && (
              event.members_only ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
                  <p className="text-gray-700 font-medium mb-2">
                    This event is for members only.
                  </p>
                  <p className="text-gray-500 text-sm mb-4">
                    An active membership is required to participate.
                  </p>
                  <Link
                    to={embed ? `/${clubid}/embed/register` : `/${clubid}/login`}
                    className="inline-block px-8 py-3 bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)] rounded-md font-semibold transition-colors"
                  >
                    {embed ? "Register for Membership" : "Register / Login"}
                  </Link>
                </div>
              ) : (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Register</h2>
                  {/* No outer <Elements> — payment step mounts its
                      own with the PI's clientSecret. */}
                  <RegistrationForm event={event} clubid={clubid!} embed={embed} />
                </div>
              )
            )}
          </div>
        </div>
      </div>
      {!embed && <Footer />}
    </div>
  );
};

export default PublicEventPage;
